import React from 'react';
import { Layout } from '@leverege/plugin';
import { CompositeTextInput, Popup } from '@leverege/ui-elements';
import I18N from '@leverege/i18n';
import S from './AttributeSelector.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('ui-attributes.condition.AttributeSelector');
const defaultFilterOptions = {
  key: 'displayName',
  get placeholder() {
    return t('placeholder');
  },
  get noResults() {
    return t('noResults');
  }
};
export default class AttributeSelector extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filterTerm: ''
    };
  }
  static defaultProps = {
    typeKey: 'name',
    nameKey: 'displayName'
  };

  /**
   * @method filterMenuItems Filters this component's `options` by some user input `filterTerm`
   * @param {Array.<object>} menuSpec What the user passed in as the `options` prop.
   * @param {string} filterTerm The term by which to filter the `menuSpec`s elements.
   * @param {Object.<string>} filterOptions The `filterOptions`, either default or user-defined.
   * The `key` in the `filterOptions` tells this function on which key of the `menuSpec` elements to filter the menus.
   * @returns {array} The filtered `menuSpec` argument.
   */
  filterMenuItems = (menuSpec, filterTerm, filterOptions) => {
    const filtered = menuSpec.filter(value => {
      const isString = typeof value[filterOptions.key] === 'string';
      return isString && value[filterOptions.key].toLowerCase().includes(filterTerm);
    });
    return filtered;
  };

  /**
   * @method onPopupChange Handle change event data when the Popup component changes
   * @param {bool} changeEvent.isOpened
   */
  onPopupChange = ({
    isOpened
  }) => {
    const {
      showFilter = true
    } = this.props;
    if (showFilter && !isOpened) {
      this.resetFilter();
    }
  };

  /**
   * @method resetFilter Reset the filter term
   */
  resetFilter = () => {
    this.setState({
      filterTerm: ''
    });
  };
  onSelect = evt => {
    const {
      eventData,
      typeKey,
      onChange
    } = this.props;
    if (onChange) {
      onChange({
        data: eventData,
        value: evt.value[typeKey]
      });
    }
  };

  /**
   * @method onChangeFilterTerm Updates the filter term
   * @param {object} event The event containing the input filter term value
   */
  onChangeFilterTerm = event => {
    const filterTerm = event.value.trim().toLowerCase();
    return this.setState({
      filterTerm
    });
  };
  createMenu = onClose => {
    const {
      filterTerm
    } = this.state;
    const {
      values,
      showFilter = true,
      filterOptions = defaultFilterOptions,
      filterVariant = 'attributeSelectorFilter'
    } = this.props;

    // Filter on each call to createMenu to ensure filtering when props may change irrespective of input changing.
    if (showFilter) {
      const filteredMenuSpec = this.filterMenuItems(values, filterTerm, filterOptions);
      const filteredMenuTree = this.createComponents(Layout.create(filteredMenuSpec), onClose);
      const noFilterResults = Array.isArray(filteredMenuTree) && filteredMenuTree.length === 0;
      return /*#__PURE__*/_jsxs(React.Fragment, {
        children: [/*#__PURE__*/_jsx(Popup.Header, {
          sticky: true,
          children: /*#__PURE__*/_jsx(CompositeTextInput, {
            autoFocus: true,
            variant: filterVariant,
            className: S.filterInput,
            onChange: this.onChangeFilterTerm,
            value: filterTerm,
            changeOnReturn: false,
            alwaysFireOnReturn: false,
            name: "filterItemLayoutSelector",
            hint: filterOptions.placeholder
          })
        }), filteredMenuTree, noFilterResults && /*#__PURE__*/_jsx(Popup.Item, {
          children: filterOptions.noResults
        })]
      });
    }
    return this.createComponents(Layout.create(values));
  };
  createComponents(rootNode, onClose) {
    const {
      eventData,
      value,
      typeKey,
      nameKey
    } = this.props;
    if (rootNode == null) {
      return null;
    }
    const r = rootNode.items.map(item => {
      if (item.type === Layout.NODE_TYPE) {
        return /*#__PURE__*/_jsx(Popup.Menu, {
          onClose: onClose,
          title: item.name,
          render: onClose => {
            return this.createComponents(item, onClose);
          }
        }, item.name);
      }
      const type = item[typeKey];
      const checked = value != null && type === value;
      return /*#__PURE__*/_jsx(Popup.Item, {
        onClose: onClose,
        checkStyle: "radio",
        checked: checked,
        value: item,
        onClick: this.onSelect,
        eventData: eventData,
        children: item[nameKey]
      }, type);
    });
    return r;
  }
  render() {
    const {
      values,
      value,
      nameKey,
      typeKey,
      className
    } = this.props;
    const selected = values.find(val => val[typeKey] === value);
    return /*#__PURE__*/_jsx(Popup, {
      setPopupMinWidth: true,
      variant: "attributeSelector|selector",
      constraints: null,
      className: className,
      toggleVariant: "selector",
      title: selected ? selected[nameKey] : t('title'),
      render: this.createMenu,
      onChange: this.onPopupChange
    });
  }
}